import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Smartphone, Copy } from 'lucide-react';

const InAppBookingsSection = ({
  voucher,
  reservations = [],
  onCopyInviteMessage,
  onWhatsAppShare
}) => {
  const [selectedInAppBooking, setSelectedInAppBooking] = useState(null);
  const [showAllReservations, setShowAllReservations] = useState(false);

  const totalCommission = (reservations || []).reduce(
    (sum, r) => sum + (r.computed_agent_cut || 0),
    0
  );

  return (
    <>
      <div className="vc-zone">
        <div className="vc-zone-label">
          <span className="vc-tag">In-App Bookings</span>
          <span className="vc-sub">what your client is doing through the app</span>
          <span className="vc-rule"></span>
        </div>

        <div
          className="vc-card vc-activity"
          onClick={() => reservations && reservations.length > 0 && setShowAllReservations(true)}
        >
          <div className="vc-card-head flex items-center justify-between pb-2 border-b border-slate-100">
            <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
              <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                <Smartphone size={15} className="text-[#067978]" />
              </span>
              <span>{voucher?.guest_information?.guest_name || "Guest"} is living this trip inside your app</span>
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="vc-count">{reservations.length} things booked</div>
              <div className="vc-count">Total Com: ₹{totalCommission}</div>
            </div>
          </div>

          {reservations && reservations.length > 0 ? (
            <>
              <div className="vc-act-grid">
                {reservations.slice(0, 3).map((r, idx) => (
                  <div
                    className="vc-act cursor-pointer hover:bg-gray-50 transition-colors"
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedInAppBooking(r);
                    }}
                  >
                    <div className="vc-cat">
                      {r.type === 'Task' ? 'Activity' : r.type || "Activity"}
                    </div>
                    <div className="vc-name">
                      {r.businessDetails?.business_name || r.name || "Item"}
                    </div>
                  </div>
                ))}
              </div>
              {reservations.length > 3 && (
                <div
                  className="vc-expand-cue cursor-pointer"
                  onClick={() => setShowAllReservations(true)}
                >
                  {`View all ${reservations.length} bookings ⌄`}
                </div>
              )}
            </>
          ) : (
            <div
              className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-teal-50/80 via-emerald-50/40 to-cyan-50/60 border border-teal-200/70 mt-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-3.5 sm:gap-4 transition-all hover:border-teal-300 w-full box-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2.5 sm:gap-3.5 text-left min-w-0 w-full md:w-auto">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-white text-[#067978] flex items-center justify-center shrink-0 border border-teal-100 shadow-xs">
                  <Smartphone size={16} className="text-[#067978]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-slate-700 leading-snug">
                    No bookings found. Share the app with your traveller
                  </div>
                  <p className="text-[10.5px] sm:text-[11.5px] text-slate-500 font-normal leading-normal mt-0.5">
                    Travellers can access digital vouchers, live itinerary &amp; explore local activities on the go.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto shrink-0 mt-1 md:mt-0">
                <button
                  type="button"
                  onClick={onWhatsAppShare}
                  className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3.5 py-2 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs font-semibold transition-all shadow-xs active:scale-95 cursor-pointer whitespace-nowrap"
                  title="Share app download link via WhatsApp"
                >
                  <span>Share via WhatsApp</span>
                </button>
                <button
                  type="button"
                  onClick={onCopyInviteMessage}
                  className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 rounded-xl text-xs font-semibold transition-all shadow-xs active:scale-95 cursor-pointer whitespace-nowrap"
                  title="Copy invite link & instructions"
                >
                  <Copy size={13} className="text-slate-500" />
                  <span>Copy Link</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Specific In-App Booking Detail Modal */}
      {typeof document !== 'undefined' && createPortal(
        <>
          <div
            className={`vc-overlay ${selectedInAppBooking ? 'vc-open' : ''}`}
            onClick={(e) => {
              if (e.target.classList.contains('vc-overlay')) setSelectedInAppBooking(null);
            }}
            style={{ zIndex: 100000 }}
          >
            {selectedInAppBooking && (
              <div
                className="vc-modal"
                style={{ padding: 0, overflow: 'hidden' }}
                role="dialog"
                aria-modal="true"
                aria-label="Booking Details"
              >
                <div className="relative h-40 bg-gray-100">
                  {(selectedInAppBooking.type === 'Task'
                    ? selectedInAppBooking.businessDetails?.activity_id?.activity_image_url?.[0] ||
                      selectedInAppBooking.businessDetails?.activity_id?.image_url?.[0] ||
                      selectedInAppBooking.businessDetails?.superadmin_activity_id?.imageUrls?.[0]
                    : selectedInAppBooking.businessDetails?.image_url?.[0] ||
                      selectedInAppBooking.businessDetails?.imageUrls?.[0]) ? (
                    <img
                      src={
                        selectedInAppBooking.type === 'Task'
                          ? selectedInAppBooking.businessDetails?.activity_id?.activity_image_url?.[0] ||
                            selectedInAppBooking.businessDetails?.activity_id?.image_url?.[0] ||
                            selectedInAppBooking.businessDetails?.superadmin_activity_id?.imageUrls?.[0]
                          : selectedInAppBooking.businessDetails?.image_url?.[0] ||
                            selectedInAppBooking.businessDetails?.imageUrls?.[0]
                      }
                      alt="Business"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-[#067978]/80 to-[#067978] flex items-center justify-center">
                      <span className="text-4xl opacity-80">📍</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <button
                      className="w-8 h-8 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 backdrop-blur-sm cursor-pointer"
                      onClick={() => setSelectedInAppBooking(null)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-6 right-6">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold mb-2 uppercase tracking-wider ${
                        selectedInAppBooking.type === 'Restaurant'
                          ? 'bg-orange-500 text-white'
                          : selectedInAppBooking.type === 'Task'
                          ? 'bg-blue-500 text-white'
                          : 'bg-purple-500 text-white'
                      }`}
                    >
                      {selectedInAppBooking.type === 'Task'
                        ? 'Activity'
                        : selectedInAppBooking.type}
                    </span>
                    <h2 className="text-2xl font-bold text-white">
                      {selectedInAppBooking.type === 'Task'
                        ? selectedInAppBooking.businessDetails?.business_name ||
                          selectedInAppBooking.businessDetails?.superadmin_activity_id?.name ||
                          selectedInAppBooking.businessDetails?.name ||
                          selectedInAppBooking.package_name ||
                          'Activity Experience'
                        : selectedInAppBooking.businessDetails?.business_name ||
                          selectedInAppBooking.businessDetails?.activity_name ||
                          selectedInAppBooking.businessDetails?.name ||
                          'Partner Business'}
                    </h2>
                  </div>
                </div>

                <div className="p-6 font-sans bg-white">
                  <div className="flex items-center justify-between pb-6 border-b border-gray-100 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200 text-lg">
                        👤
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-0.5">
                          Guest
                        </p>
                        <p className="text-sm font-semibold text-[#4b5563] leading-none">
                          {selectedInAppBooking.guestName ||
                            voucher?.guest_information?.guest_name ||
                            'Guest'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                          selectedInAppBooking.status === 'Confirmed'
                            ? 'bg-blue-50 border-blue-200 text-blue-600'
                            : selectedInAppBooking.status === 'Completed'
                            ? 'bg-green-50 border-green-200 text-green-600'
                            : 'bg-yellow-50 border-yellow-200 text-yellow-600'
                        }`}
                      >
                        <span className="text-lg leading-none pt-0.5">📌</span>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-0.5">
                          Status
                        </p>
                        <p className="text-sm font-semibold text-[#4b5563] leading-none">
                          {selectedInAppBooking.status}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Date
                      </p>
                      <div className="flex items-center gap-2 text-[#4b5563] font-semibold text-sm">
                        📅{' '}
                        {selectedInAppBooking.date || selectedInAppBooking.createdAt
                          ? new Date(
                              selectedInAppBooking.date || selectedInAppBooking.createdAt
                            ).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Time
                      </p>
                      <div className="flex items-center gap-2 text-[#4b5563] font-semibold text-sm">
                        ⏰ {selectedInAppBooking.bookedTime || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Created At
                      </p>
                      <div className="flex items-center gap-2 text-[#4b5563] font-semibold text-sm">
                        🕒{' '}
                        {selectedInAppBooking.createdAt
                          ? new Date(selectedInAppBooking.createdAt).toLocaleString()
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Total Members
                      </p>
                      <div className="flex items-center gap-2 text-[#4b5563] font-semibold text-sm">
                        👥 {selectedInAppBooking.totalMembers || 1}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Advance Paid
                      </p>
                      <div className="flex items-center gap-2 text-[#4b5563] font-semibold text-sm">
                        ₹ {selectedInAppBooking.advance_Amt || 0}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Bill Amount
                      </p>
                      <div className="flex items-center gap-2 text-gray-900 font-bold text-sm">
                        ₹ {selectedInAppBooking.final_bill_amount || 0}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-bold mb-1">
                        Your Commission
                      </p>
                      <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                        ₹ {selectedInAppBooking.computed_agent_cut || 0}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end items-center border-t border-gray-100">
                  <button
                    onClick={() => setSelectedInAppBooking(null)}
                    className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold rounded-xl transition-colors uppercase tracking-wider active:scale-95 cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* In-App Bookings List Modal */}
          <div
            className={`vc-overlay ${showAllReservations ? 'vc-open' : ''}`}
            onClick={(e) => {
              if (e.target.classList.contains('vc-overlay')) setShowAllReservations(false);
            }}
          >
            <div
              className="vc-modal !max-w-xl"
              role="dialog"
              aria-modal="true"
              aria-label="In-App Bookings"
            >
              <div className="vc-m-head">
                <h4 className="flex items-center gap-2">
                  <Smartphone size={16} className="text-[#067978]" />
                  <span>In-App Bookings</span>
                </h4>
                <button className="vc-m-close" onClick={() => setShowAllReservations(false)}>
                  ✕
                </button>
              </div>
              <div className="vc-m-body">
                <div className="grid gap-4">
                  {(reservations || []).map((res, i) => (
                    <div
                      key={i}
                      className="vc-card cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowAllReservations(false);
                        setSelectedInAppBooking(res);
                      }}
                    >
                      <div className="vc-card-head mb-0 border-none pb-0">
                        <div className="flex items-center gap-3">
                          <div className="vc-ic">
                            {res.type?.toLowerCase() === 'restaurant'
                              ? '🍽️'
                              : res.type?.toLowerCase() === 'shop'
                              ? '🛍️'
                              : '🎟️'}
                          </div>
                          <div>
                            <div className="text-[10px] font-bold tracking-widest uppercase text-[#8a9aa5] mb-1">
                              {res.type === 'Task' ? 'ACTIVITY' : res.type || "ACTIVITY"}
                            </div>
                            <div className="text-[15px] font-semibold text-[#4b5563]">
                              {res.businessDetails?.business_name || res.name || "Item"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(reservations || []).length === 0 && (
                    <div className="text-sm text-[#8a9aa5] text-center">
                      No in-app bookings found.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default InAppBookingsSection;
